import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from './lib/api';

interface ExerciceSummary {
  id: string;
  debut: string;
  fin: string;
  statut: string;
  is_current: boolean;
}

interface ComparisonMetric {
  a: number;
  b: number;
  delta: number;
  delta_pct: number | null;
}

interface ComparisonData {
  exercice_a: ExerciceSummary;
  exercice_b: ExerciceSummary;
  comparison: {
    ca: ComparisonMetric;
    charges: ComparisonMetric;
    resultat: ComparisonMetric;
    tva_nette: ComparisonMetric;
  };
}

function formatCentimes(centimes: number): string {
  return (centimes / 100).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
}

function formatPct(pct: number | null): string {
  if (pct === null) return '—';
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(1)} %`;
}

function formatExerciceLabel(ex: ExerciceSummary): string {
  const debut = new Date(ex.debut).toLocaleDateString('fr-FR');
  const fin = new Date(ex.fin).toLocaleDateString('fr-FR');
  return `${debut} → ${fin}${ex.is_current ? ' (en cours)' : ''}`;
}

export default function ExerciceComparison({ entrepriseId }: { entrepriseId: string }) {
  const [exerciceA, setExerciceA] = useState('');
  const [exerciceB, setExerciceB] = useState('');

  const { data: exercicesList } = useQuery({
    queryKey: ['exercices', entrepriseId],
    queryFn: async () => {
      const list = await api.exercices.list(entrepriseId);
      if (list.length >= 2 && !exerciceA && !exerciceB) {
        setExerciceA(list[list.length - 2].id);
        setExerciceB(list[list.length - 1].id);
      }
      return list;
    },
  });
  const exercices: ExerciceSummary[] = exercicesList || [];

  const canCompare = exerciceA && exerciceB && exerciceA !== exerciceB;
  const { data, isLoading: loading, error: compError } = useQuery({
    queryKey: ['comparison', entrepriseId, exerciceA, exerciceB],
    queryFn: () => api.comparison.get(entrepriseId, exerciceA, exerciceB),
    enabled: !!canCompare,
  });
  const error = compError ? (compError as Error).message : null;

  const rows: { label: string; key: keyof ComparisonData['comparison'] }[] = [
    { label: 'Chiffre d\'affaires', key: 'ca' },
    { label: 'Charges', key: 'charges' },
    { label: 'Résultat', key: 'resultat' },
    { label: 'TVA nette', key: 'tva_nette' },
  ];

  const resultatImproved = data
    ? data.comparison.resultat.b > data.comparison.resultat.a
    : null;

  const inputClass = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent";

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Comparaison inter-exercices</h2>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Exercice A</label>
            <select value={exerciceA} onChange={(e) => setExerciceA(e.target.value)} className={inputClass}>
              <option value="">— Sélectionner —</option>
              {exercices.map((ex) => (
                <option key={ex.id} value={ex.id}>{formatExerciceLabel(ex)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Exercice B</label>
            <select value={exerciceB} onChange={(e) => setExerciceB(e.target.value)} className={inputClass}>
              <option value="">— Sélectionner —</option>
              {exercices.map((ex) => (
                <option key={ex.id} value={ex.id}>{formatExerciceLabel(ex)}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {error && <div className="text-red-700 bg-red-50 border border-red-200 px-4 py-3 rounded-lg text-sm mb-4">{error}</div>}
      {loading && <p className="text-sm text-gray-500">Chargement...</p>}

      {data && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Indicateur</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Exercice A</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Exercice B</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Variation</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">%</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ label, key }) => {
                const metric = data.comparison[key];
                const isResultat = key === 'resultat';
                return (
                  <tr key={key} className={`hover:bg-gray-50 transition-colors ${isResultat ? 'font-bold' : ''}`}>
                    <td className={`px-4 py-3 border-b border-gray-100 ${isResultat && resultatImproved !== null ? (resultatImproved ? 'text-green-600' : 'text-red-600') : 'text-gray-700'}`}>{label}</td>
                    <td className="px-4 py-3 text-gray-700 border-b border-gray-100 text-right">{formatCentimes(metric.a)}</td>
                    <td className="px-4 py-3 text-gray-700 border-b border-gray-100 text-right">{formatCentimes(metric.b)}</td>
                    <td className="px-4 py-3 text-gray-700 border-b border-gray-100 text-right">{formatCentimes(metric.delta)}</td>
                    <td className={`px-4 py-3 border-b border-gray-100 text-right ${metric.delta_pct !== null && metric.delta_pct > 0 ? 'text-green-600' : metric.delta_pct !== null && metric.delta_pct < 0 ? 'text-red-600' : 'text-gray-500'}`}>{formatPct(metric.delta_pct)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!data && !loading && exerciceA && exerciceB && exerciceA === exerciceB && (
        <p className="text-sm text-gray-500">Veuillez sélectionner deux exercices différents.</p>
      )}

      {exercices.length < 2 && (
        <p className="text-sm text-gray-500">Il faut au moins deux exercices pour effectuer une comparaison.</p>
      )}
    </div>
  );
}
